import { Link } from 'react-router-dom';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { ErrorIllustration, EmptyIllustration } from 'shared/assets/illustrations';

export function PageHeader({ title, description, actions, kicker }) {
  return (
    <header className="page-header">
      <div>
        {kicker ? <p className="kicker">{kicker}</p> : null}
        <h1>{title}</h1>
        {description ? <p className="muted">{description}</p> : null}
      </div>
      {actions ? <div className="page-header-actions">{actions}</div> : null}
    </header>
  );
}

export function EmptyState({ title, action, illustration = true }) {
  return (
    <div className="state-block empty-state">
      {illustration ? <EmptyIllustration /> : null}
      <p>{title}</p>
      {action}
    </div>
  );
}

export function ErrorState({ title, onRetry, retryLabel, homeAction = true }) {
  const { t } = useI18n();
  return (
    <div className="state-block error-state" role="alert">
      <ErrorIllustration />
      <p>{title}</p>
      <div className="action-row">
        {onRetry ? (
          <button type="button" className="btn btn-secondary" onClick={onRetry}>
            {retryLabel}
          </button>
        ) : null}
        {homeAction ? (
          <Link className="btn btn-ghost" to={paths.home}>{t('notFound.home')}</Link>
        ) : null}
      </div>
    </div>
  );
}

export function Skeleton({ rows = 4, variant = 'lines' }) {
  const { t } = useI18n();
  return (
    <div className={`skeleton-stack skeleton-${variant}`} role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('status.loading')}</span>
      <div aria-hidden="true">
        {variant === 'cards' ? (
          Array.from({ length: rows }).map((_, index) => (
            <div key={index} className="skeleton-card" />
          ))
        ) : (
          Array.from({ length: rows }).map((_, index) => (
            <div key={index} className="skeleton-row" />
          ))
        )}
      </div>
    </div>
  );
}

export function Card({ children, className = '', interactive = false, as: Tag = 'section', ...props }) {
  return (
    <Tag className={`card ${interactive ? 'card-interactive' : ''} ${className}`.trim()} {...props}>
      {children}
    </Tag>
  );
}

export function StatusChip({ children, tone = 'neutral' }) {
  return <span className={`chip chip-${tone}`}>{children}</span>;
}

export function HeroBand({ kicker, title, body, actions, art, tone = 'page' }) {
  return (
    <section className={`hero-band hero-band-${tone}`}>
      <div className="hero-band-copy">
        {kicker ? <p className="kicker">{kicker}</p> : null}
        {title ? <h1>{title}</h1> : null}
        {body ? <p className="lead">{body}</p> : null}
        {actions ? <div className="action-row">{actions}</div> : null}
      </div>
      {art ? <div className="hero-band-art">{art}</div> : null}
    </section>
  );
}

export function MobileActionBar({ children, status }) {
  return (
    <div className="mobile-action-bar">
      {status ? <div className="mobile-action-status">{status}</div> : null}
      <div className="mobile-action-row">{children}</div>
    </div>
  );
}
